/**
 * RunPerformance.tsx — Analytics / Run Performance page (Closr style)
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { SectionTitle, LoadingState, ErrorState } from '../DashboardShared'
import { WorkspaceMetric } from './OverviewHome'
import { useAsync, fetchRunPerformance } from '../api'
import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Legend
} from 'recharts'
import { accent, danger, ink, edge, shadow, surface, warning } from '../../../theme/tokens'

export function RunPerformance() {
  const { data, loading, error } = useAsync(fetchRunPerformance, [])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const perf = data ?? { daily: [], agents: [] }
  const total = perf.daily.reduce((s, d) => s + d.total, 0)
  const succeeded = perf.daily.reduce((s, d) => s + d.succeeded, 0)
  const failed = perf.daily.reduce((s, d) => s + d.failed, 0)
  const rate = total > 0 ? ((succeeded / total) * 100).toFixed(1) : '0.0'

  return (
    <div className="page-enter flex flex-col gap-5">
      <SectionTitle title="Run Performance" subtitle="10-day view of run volume, success rate, and per-agent breakdown" />

      <div className="grid grid-cols-4 gap-3">
        <WorkspaceMetric label="Total Runs (10d)" value={total} />
        <WorkspaceMetric label="Succeeded" value={succeeded} />
        <WorkspaceMetric label="Failed" value={failed} />
        <WorkspaceMetric label="Success Rate" value={`${rate}%`} />
      </div>

      <div className="k-card">
        <div className="k-card-head">
          <div>
            <div className="k-card-title">Daily Run Volume</div>
            <div className="k-card-sub">Last 10 days</div>
          </div>
        </div>
        <div className="p-5">
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={perf.daily}>
              <defs>
                <linearGradient id="gradSucceeded" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={accent.soft} stopOpacity={0.4} />
                  <stop offset="95%" stopColor={accent.soft} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradFailed" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={danger.DEFAULT} stopOpacity={0.15} />
                  <stop offset="95%" stopColor={danger.DEFAULT} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={edge['row-faint']} vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: ink.muted }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: ink.muted }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: surface.card, border: 'none', borderRadius: 10, fontSize: 12, boxShadow: shadow.tooltip }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="succeeded" name="Succeeded" stroke={accent.DEFAULT} strokeWidth={2} fill="url(#gradSucceeded)" />
              <Area type="monotone" dataKey="failed" name="Failed" stroke={danger.DEFAULT} strokeWidth={2} fill="url(#gradFailed)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="k-card">
        <div className="k-card-head">
          <div className="k-card-title">Per-Agent Breakdown</div>
        </div>
        <table className="closr-table dash-table w-full">
          <thead>
            <tr>
              <th className="text-left">Agent</th>
              <th className="text-left">Total Runs</th>
              <th className="text-left">Succeeded</th>
              <th className="text-left">Failed</th>
              <th className="text-left">Avg Duration</th>
              <th className="text-left">Success Rate</th>
            </tr>
          </thead>
          <tbody>
            {perf.agents.map(a => (
              <tr key={a.name}>
                <td className="font-medium text-ink-primary">{a.name}</td>
                <td className="font-mono text-12">{a.total}</td>
                <td className="font-mono text-12 text-success">{a.succeeded}</td>
                <td className="font-mono text-12 text-danger">{a.failed}</td>
                <td className="font-mono text-12 text-ink-secondary">{a.avgDuration}</td>
                <td>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-track rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{
                        width: `${a.successRate}%`,
                        background: a.successRate >= 90 ? accent.DEFAULT : a.successRate >= 75 ? warning.DEFAULT : danger.DEFAULT
                      }} />
                    </div>
                    <span className="text-12 font-mono">{a.successRate}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
